// src/app/services/websocket.service.ts
import { Injectable } from '@angular/core';
import { HubConnection, HubConnectionBuilder, HubConnectionState, LogLevel } from '@microsoft/signalr';
import { ResultsFetcherService } from '@services/results-fetcher/results-fetcher.service';
import { SolutionAction } from '@state/solution.state';
import { ConfigService } from 'app/app-config.service';
import { Store } from '@ngxs/store';
import { AdvancedMeasureAction } from '@state/advanced-measure.state';
import { QuestionnaireAction } from '@state/questionnaire.state';
import { EmissionFactorItemsAction } from '@state/organizationItems.state';
import { fetchAuthSession } from 'aws-amplify/auth';
import { HubAccessDeniedError } from './hub-access-denied.error';

@Injectable({
    providedIn: 'root',
})
export class ResultWebSocketService {
    private hubConnection: HubConnection | null = null;
    private startPromise: Promise<void> | null = null;
    private currentOrganizationId: string | null = null;
    private currentQuestionnaireId: string | null = null;

    private readonly handleVisibilityChange = async (): Promise<void> => {
        if (document.visibilityState === 'visible' && this.hubConnection?.state === HubConnectionState.Disconnected) {
            try {
                await this.hubConnection.start();
                if (this.currentOrganizationId) {
                    await this.joinOrganizationGroup(this.currentOrganizationId);
                }
                if (this.currentQuestionnaireId) {
                    await this.joinQuestionnaireGroup(this.currentQuestionnaireId);
                }
            } catch (err) {
                console.error('❌ WebSocket reconnection on visibility failed:', err);
            }
        }
    };

    constructor(
        private configService: ConfigService,
        private resultFetcherService: ResultsFetcherService,
        private store: Store,
    ) {}

    async startConnection(): Promise<void> {
        if (this.hubConnection?.state === HubConnectionState.Connected) {
            return;
        }

        if (this.startPromise) {
            return this.startPromise;
        }

        if (this.hubConnection?.state === HubConnectionState.Disconnected) {
            this.startPromise = this.hubConnection
                .start()
                .then(async () => {
                    if (this.currentOrganizationId) {
                        await this.joinOrganizationGroup(this.currentOrganizationId);
                    }
                })
                .catch((error: unknown) => {
                    console.error('❌ WebSocket reconnection failed:', error);
                })
                .finally(() => {
                    this.startPromise = null;
                });
            return this.startPromise;
        }

        this.hubConnection = new HubConnectionBuilder()
            .withUrl(`${this.configService.getConfig.apiUrl}/result-cache-invalidation`, {
                accessTokenFactory: () => fetchAuthSession().then((s) => s.tokens?.idToken?.toString() ?? ''),
            })
            .withAutomaticReconnect()
            .configureLogging(LogLevel.Information)
            .build();

        this.hubConnection.on('ResultsCacheInvalidated', () => {
            this.resultFetcherService.setInvalid();
        });

        this.hubConnection.on('SolutionsInvalidated', () => {
            this.store.dispatch(new SolutionAction.Invalidate());
        });

        this.hubConnection.on('AdvancedMeasureInvalidated', () => {
            // Expert-side edits invalidate the cached /result just as scan-side edits do —
            // without this, the summary matrix keeps the pre-edit summaryDoc until something
            // else forces a recompute (the scan-side path fires ResultsCacheInvalidated, which
            // does call setInvalid; this handler used to forget the symmetric step).
            this.resultFetcherService.setInvalid();
            this.store.dispatch(new AdvancedMeasureAction.ClearState());
            this.store.dispatch(new QuestionnaireAction.InvalidateSummaryDoc());
        });

        this.hubConnection.on('OrganizationEmissionFactorsDatabasesInvalidated', () => {
            this.store.dispatch(new EmissionFactorItemsAction.InvalidateAllDatabases());
        });

        this.hubConnection.onreconnected(() => {
            if (this.currentOrganizationId) {
                void this.joinOrganizationGroup(this.currentOrganizationId);
            }
            if (this.currentQuestionnaireId) {
                void this.joinQuestionnaireGroup(this.currentQuestionnaireId);
            }
        });

        document.addEventListener('visibilitychange', this.handleVisibilityChange);

        this.startPromise = this.hubConnection
            .start()
            .catch((error: unknown) => {
                console.error('❌ WebSocket connection failed:', error);
            })
            .finally(() => {
                this.startPromise = null;
            });

        return this.startPromise;
    }

    async joinOrganizationGroup(organizationId: string): Promise<void> {
        this.currentOrganizationId = organizationId;
        if (!this.hubConnection) {
            return;
        }
        try {
            await this.hubConnection.invoke('JoinOrganizationGroup', organizationId);
        } catch (err: unknown) {
            if (err instanceof Error && err.message.includes('Access denied')) {
                throw new HubAccessDeniedError('JoinOrganizationGroup', err.message, err);
            }

            console.error('❌ Failed to join organization group:', err);
        }
    }

    async joinQuestionnaireGroup(questionnaireId: string): Promise<void> {
        this.currentQuestionnaireId = questionnaireId;
        if (!this.hubConnection) {
            return;
        }
        try {
            await this.hubConnection.invoke('JoinQuestionnaireGroup', questionnaireId);
        } catch (err: unknown) {
            if (err instanceof Error && err.message.includes('Access denied')) {
                throw new HubAccessDeniedError('JoinQuestionnaireGroup', err.message, err);
            }

            console.error('❌ Failed to join questionnaire group:', err);
        }
    }

    async leaveQuestionnaireGroup(questionnaireId: string): Promise<void> {
        this.currentQuestionnaireId = null;
        if (this.hubConnection) {
            try {
                await this.hubConnection.invoke('LeaveQuestionnaireGroup', questionnaireId);
            } catch (error) {
                console.error('❌ Failed to leave questionnaire group:', error);
            }
        }
    }

    async stopConnection(): Promise<void> {
        document.removeEventListener('visibilitychange', this.handleVisibilityChange);
        this.currentOrganizationId = null;
        this.currentQuestionnaireId = null;
        if (this.hubConnection) {
            await this.hubConnection.stop();
        }
    }
}
